import styled from '@emotion/styled';
import { spacer, transition } from '@tokens';
import { forwardRef } from 'react';
import { Icon } from '@components/Icon/Icon';
import { css } from '@emotion/react';
import { baseInputGhostContainer } from '@components/Inputs/styles';
import { Badge } from '@components/Badge/Badge';
import { PlanBadge } from '@components/PlanBadge/PlanBadge';
import type { DropdownSelectTriggerProps } from './types';

const SELECT_HEIGHT = {
  s: 32,
  m: 40,
  l: 56,
};

interface StyledButtonProps {
  isInvalid: boolean;
  disabled: boolean;
  selectSize: 's' | 'm' | 'l';
  isIconPresent: boolean;
}

const StyledButton = styled.button<StyledButtonProps>`
  ${({ isInvalid, disabled }) =>
    baseInputGhostContainer({
      isInvalid,
      isDisabled: disabled,
      isReadonly: false,
    })};
  height: ${({ selectSize }) => SELECT_HEIGHT[selectSize]}px;
  padding: 0;
  padding-left: ${({ isIconPresent }) =>
    isIconPresent ? spacer.x5 : spacer.x2};
  padding-right: ${({ isInvalid }) => (isInvalid ? spacer.x8 : spacer.x5)};
  ${transition()};
  appearance: none;
  border: none;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;

  position: relative;

  &:focus-visible {
    outline: none;
  }
`;

const InputIcon = styled(Icon)`
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  pointer-events: none;
`;

const DivLabel = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: auto;
`;

const getIconColor = ({
  isInvalid,
  isDisabled,
}: {
  isInvalid: boolean;
  isDisabled: boolean;
}) => {
  if (isInvalid) {
    return 'text_danger';
  } else if (isDisabled) {
    return 'text_disabled';
  }

  return 'text_default';
};

export const DropdownSelectTrigger = forwardRef<
  HTMLButtonElement,
  DropdownSelectTriggerProps
>(
  (
    {
      selectedOption,
      isDisabled,
      isInvalid,
      size = 'm',
      icon,
      className,
      placeholder,
      badge,
      planBadge,
      ...props
    }: DropdownSelectTriggerProps,
    ref,
  ): JSX.Element => (
    <StyledButton
      {...props}
      className={className}
      ref={ref}
      isInvalid={isInvalid}
      disabled={isDisabled}
      isIconPresent={!!icon}
      selectSize={size}
      type="button"
    >
      <DivLabel>{selectedOption?.label ?? placeholder}</DivLabel>
      {badge && <Badge kind={badge} size="s" />}
      {planBadge && <PlanBadge kind={planBadge} size="s" />}
      {!!icon && (
        <InputIcon
          icon={icon}
          size="m"
          color={getIconColor({ isInvalid, isDisabled })}
          css={css`
            left: ${spacer.x2};
          `}
          data-testid="custom-icon"
        />
      )}

      {isInvalid && (
        <InputIcon
          icon="warning_alt"
          size="m"
          color="text_danger"
          css={css`
            right: ${spacer.x5};
          `}
          data-testid="invalid-icon"
        />
      )}
      {!isDisabled && (
        <InputIcon
          icon="caret_down_alt"
          size="m"
          color="text_light"
          css={css`
            right: ${spacer.x2};
          `}
        />
      )}
    </StyledButton>
  ),
);

DropdownSelectTrigger.displayName = 'DropdownSelectTrigger';

DropdownSelectTrigger.defaultProps = {
  size: 'm',
  isDisabled: false,
  isInvalid: false,
};
